import { useId, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { SecretRevealDialog, type RevealedSecret } from '@/components/SecretRevealDialog';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectOption } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatAgo } from '@/lib/ago';
import { api } from '@/lib/api';
import { useDialogFocusRestore } from '@/lib/hooks/useDialogFocusRestore';
import { usePolledData } from '@/lib/hooks/usePolledData';
import { webhookErrorMessage, SIGNING_SECRET_ACK, SIGNING_SECRET_WARNING } from '@/lib/webhooks';
import type { WebhookDelivery, WebhookDeliveryStatus, WebhookReceiver, WebhookReceiverWithSecret } from '@/types/webhook';

const DELIVERY_LIMIT = 50;

const STATUS_FILTERS: { value: '' | WebhookDeliveryStatus; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
];

function rotatedReveal(r: WebhookReceiverWithSecret, seq: number): RevealedSecret {
  return {
    id: `rotate:${r.id}:${seq}`,
    title: 'Signing secret rotated',
    description: `Copy the new secret for '${r.name}' now. It will not be shown again.`,
    label: 'Signing secret',
    value: r.secret,
    warning: `${SIGNING_SECRET_WARNING} The previous secret no longer works: every attempt from now on, including retries, is signed with this one.`,
    acknowledgement: SIGNING_SECRET_ACK,
  };
}

interface WebhookDeliveriesDialogProps {
  /** null keeps the dialog closed. */
  receiver: WebhookReceiver | null;
  onClose: () => void;
}

/** Shell always mounted; the body mounts per receiver so the filter and busy state reset on every open. */
export function WebhookDeliveriesDialog({ receiver, onClose }: WebhookDeliveriesDialogProps) {
  const { captureOpener, restoreFocus } = useDialogFocusRestore();
  return (
    <Dialog open={receiver !== null} onOpenChange={(next) => !next && onClose()}>
      {receiver ? (
        <DialogContent
          className="max-h-[90vh] overflow-y-auto sm:max-w-4xl"
          onOpenAutoFocus={captureOpener}
          onCloseAutoFocus={restoreFocus}
        >
          <DeliveriesBody key={receiver.id} receiver={receiver} />
        </DialogContent>
      ) : null}
    </Dialog>
  );
}

function DeliveriesBody({ receiver }: { receiver: WebhookReceiver }) {
  const filterId = useId();
  const inactive = !receiver.is_active;
  const [status, setStatus] = useState<'' | WebhookDeliveryStatus>('');
  // Bumped after Send test / Redeliver so the table refetches right away.
  const [refreshSeq, setRefreshSeq] = useState(0);
  // Inline, so a refused action (e.g. an inactive receiver) stays visible next to the buttons.
  const [actionError, setActionError] = useState<string | null>(null);
  const [testBusy, setTestBusy] = useState(false);
  const [redeliverId, setRedeliverId] = useState<number | null>(null);
  const busyRef = useRef(false);

  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotateBusy, setRotateBusy] = useState(false);
  const [rotateError, setRotateError] = useState<string | null>(null);
  const rotateBusyRef = useRef(false);
  // The rotated secret waits here until the confirm has closed and focus is back on "Rotate secret",
  // so the reveal returns focus there too.
  const [rotated, setRotated] = useState<RevealedSecret | null>(null);
  const [reveal, setReveal] = useState<RevealedSecret | null>(null);
  const revealSeq = useRef(0);

  const sendTest = async () => {
    if (busyRef.current || inactive) return;
    busyRef.current = true;
    setTestBusy(true);
    setActionError(null);
    try {
      await api.webhooks.test(receiver.id);
      toast.success('Test event queued');
      setRefreshSeq((n) => n + 1);
    } catch (err) {
      setActionError(webhookErrorMessage(err, 'Failed to send test event'));
    } finally {
      busyRef.current = false;
      setTestBusy(false);
    }
  };

  const redeliver = async (delivery: WebhookDelivery) => {
    if (busyRef.current || inactive) return;
    busyRef.current = true;
    setRedeliverId(delivery.id);
    setActionError(null);
    try {
      await api.webhooks.redeliver(delivery.id);
      toast.success('Redelivery queued');
      setRefreshSeq((n) => n + 1);
    } catch (err) {
      setActionError(webhookErrorMessage(err, 'Failed to redeliver'));
    } finally {
      busyRef.current = false;
      setRedeliverId(null);
    }
  };

  const rotate = async () => {
    if (rotateBusyRef.current) return;
    rotateBusyRef.current = true;
    setRotateBusy(true);
    setRotateError(null);
    try {
      const data = await api.webhooks.rotateSecret(receiver.id);
      setRotated(rotatedReveal(data, ++revealSeq.current));
      setRotateOpen(false);
    } catch (err) {
      setRotateError(webhookErrorMessage(err, 'Failed to rotate secret'));
    } finally {
      rotateBusyRef.current = false;
      setRotateBusy(false);
    }
  };

  const busy = testBusy || redeliverId !== null;

  return (
    <>
      <DialogHeader>
        <DialogTitle className="[overflow-wrap:anywhere]">Deliveries: {receiver.name}</DialogTitle>
        <DialogDescription className="font-mono text-xs [overflow-wrap:anywhere]">{receiver.url}</DialogDescription>
      </DialogHeader>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <Button size="sm" aria-disabled={busy || inactive} aria-busy={testBusy} onClick={() => void sendTest()}>
            {testBusy ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
            {testBusy ? 'Sending…' : 'Send test'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setRotateError(null);
              setRotateOpen(true);
            }}
          >
            Rotate secret
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor={filterId} className="text-sm text-muted-foreground">
            Status
          </label>
          <Select id={filterId} className="w-36" value={status} onValueChange={(v) => setStatus(v as '' | WebhookDeliveryStatus)}>
            {STATUS_FILTERS.map((f) => (
              <SelectOption key={f.value} value={f.value}>
                {f.label}
              </SelectOption>
            ))}
          </Select>
        </div>
      </div>

      {inactive ? (
        <p role="note" className="rounded-lg border border-status-warning-soft bg-status-warning-soft p-3 text-sm text-status-warning">
          This receiver is inactive. Activate it (Edit → Active) before sending a test or redelivering.
        </p>
      ) : null}

      {actionError ? (
        <p role="alert" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive [overflow-wrap:anywhere]">
          {actionError}
        </p>
      ) : null}

      {/* Keyed by filter: each filter starts in its own loading state instead of showing the previous filter's rows. */}
      <DeliveriesTable
        key={status}
        receiverId={receiver.id}
        status={status}
        refreshSeq={refreshSeq}
        actionsDisabled={busy || inactive}
        redeliverId={redeliverId}
        onRedeliver={(d) => void redeliver(d)}
      />

      <ConfirmDialog
        open={rotateOpen}
        onOpenChange={(next) => {
          if (!next && !rotateBusyRef.current) {
            setRotateOpen(false);
            setRotateError(null);
          }
        }}
        title="Rotate signing secret?"
        description={`Rotate the secret of '${receiver.name}'? The current secret stops working immediately, so update your receiver with the new one.`}
        confirmLabel={rotateBusy ? 'Rotating…' : 'Rotate'}
        destructive
        loading={rotateBusy}
        error={rotateError}
        onConfirm={() => void rotate()}
        onClosed={() => {
          if (!rotated) return;
          setReveal(rotated);
          setRotated(null);
        }}
      />

      <SecretRevealDialog reveal={reveal} onDone={() => setReveal(null)} />
    </>
  );
}

interface DeliveriesTableProps {
  receiverId: number;
  status: '' | WebhookDeliveryStatus;
  refreshSeq: number;
  actionsDisabled: boolean;
  redeliverId: number | null;
  onRedeliver: (delivery: WebhookDelivery) => void;
}

function DeliveriesTable({ receiverId, status, refreshSeq, actionsDisabled, redeliverId, onRedeliver }: DeliveriesTableProps) {
  const deliveries = usePolledData(
    () => api.webhooks.deliveries(receiverId, { status: status || undefined, limit: DELIVERY_LIMIT }),
    5000,
    refreshSeq,
  );
  const page = deliveries.data;
  const rows = page?.items ?? [];
  // Poll time, not Date.now(): render stays pure and the "ago" labels advance with each poll.
  const now = deliveries.updatedAt ?? undefined;

  if (deliveries.loading) {
    return (
      <p role="status" className="py-8 text-center text-muted-foreground">
        Loading deliveries…
      </p>
    );
  }
  if (page === undefined) {
    return (
      <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
        <span>{deliveries.error ?? 'Failed to load deliveries'}</span>
        <Button size="sm" variant="outline" onClick={deliveries.refetch}>
          Retry
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {deliveries.error ? (
        <p role="alert" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
          Could not refresh deliveries: {deliveries.error}
        </p>
      ) : null}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Event</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Attempts</TableHead>
            <TableHead>Last response</TableHead>
            <TableHead>Time</TableHead>
            <TableHead className="text-right">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                {status ? `No ${status} deliveries.` : 'No deliveries yet. Send a test to check the receiver.'}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((d) => {
              const iso = d.delivered_at ?? d.created_at;
              const t = Date.parse(iso);
              return (
                <TableRow key={d.id}>
                  <TableCell className="font-mono text-xs whitespace-nowrap">
                    {d.event?.type ?? 'event'} <span className="text-muted-foreground">#{d.event_id}</span>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={d.status} />
                  </TableCell>
                  <TableCell className="text-sm">{d.attempts}</TableCell>
                  <TableCell className="max-w-[18rem] text-sm">
                    <div className="flex min-w-0 items-baseline gap-2">
                      <span className="font-mono">{d.last_status_code || '—'}</span>
                      {d.last_error ? (
                        <span className="truncate text-muted-foreground" title={d.last_error}>
                          {d.last_error}
                        </span>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap">
                    {Number.isNaN(t) ? (
                      '—'
                    ) : (
                      <time dateTime={iso} title={new Date(t).toLocaleString()}>
                        {formatAgo(t, now)}
                      </time>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {d.status === 'failed' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        aria-label={`Redeliver ${d.event?.type ?? 'event'} #${d.event_id}`}
                        aria-disabled={actionsDisabled}
                        aria-busy={redeliverId === d.id}
                        onClick={() => onRedeliver(d)}
                      >
                        {redeliverId === d.id ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
                        Redeliver
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      {page.total > rows.length ? (
        <p className="text-xs text-muted-foreground">
          Showing the latest {rows.length} of {page.total} deliveries.
        </p>
      ) : null}
    </div>
  );
}
